import {
  BrowserIcon,
  Flag,
  LinkButton,
  OperatingSystemIcon,
  RelativeTime,
  Stack,
  Typography,
  WithTooltip,
} from '@compass/components'
import { cn } from '@compass/utils'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import { useEnterAnimation, useExitAnimation } from '@react-aria/utils'
import { AppRoute, buildRoute } from 'appRoutes'
import { LinkButtonLink } from 'components/compass-integration'
import { VisitorIdLink } from 'components/VisitorIdLink'
import { useCurrentSubscription, useCurrentSubscriptionData } from 'features/subscription'
import { formatDateTimeDistance, formatNum, formatPercent } from 'helpers/format'
import { useToast } from 'hooks'
import useNormalizedDateTime from 'hooks/useNormalizedDateTime'
import { CornerDownRight, SearchIcon } from 'lucide-react'
import { GeolocationV4, IdEventV4 } from 'models/event'
import {
  ComponentProps,
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { twMerge } from 'tailwind-merge'

import { useGetEnvironmentName } from '../../hooks/useGetEnvironmentName'
import { CopyButton } from './CopyButton'
import { IpLocationMap } from './IpLocationMap'
import { onQuickFilter } from './onQuickFilter'
import { usePersistedEventFilterParams } from './usePersistedEventFilterParams'

function Column({ children }: PropsWithChildren) {
  return <div className={cn('flex flex-col flex-1 gap-6')}>{children}</div>
}

function SectionLayout({
  children,
  className,
  fullWidth = true,
}: PropsWithChildren<{ className?: string; fullWidth?: boolean }>) {
  // TODO: refactor with <Stack> when conflicting var values is resolved
  return <dl className={cn('flex flex-col gap-4 sm:gap-2', fullWidth && 'fullWidth', className)}>{children}</dl>
}

function Row({
  children,
  shouldBeVisible = null,
  className,
}: PropsWithChildren<{ shouldBeVisible?: boolean | undefined | null; className?: string }>) {
  const ref = useRef<HTMLDivElement>(null)
  const constantElement = shouldBeVisible === null

  const isExiting = useExitAnimation(ref, shouldBeVisible ?? false) || false
  if (shouldBeVisible === false && !isExiting) {
    return null
  }

  return (
    <RowInner ref={ref} isExiting={isExiting} animated={!constantElement} className={className}>
      {children}
    </RowInner>
  )
}

const ACCORDION_PADDING_BOTTOM = 0
const RowInner = forwardRef(function RowInner(
  {
    animated,
    children,
    isExiting,
    className,
  }: PropsWithChildren<{ animated: boolean; isExiting?: boolean; className?: string }>,
  rowRef: RefObject<HTMLDivElement>
) {
  const isEntering = useEnterAnimation(rowRef) ?? false

  useEffect(() => {
    if (rowRef.current) {
      rowRef.current.style.setProperty('--accordion-content-height', `${Math.max(200, rowRef.current.scrollHeight)}px`)
    }
  }, [isEntering, isExiting, rowRef])

  return (
    <div
      ref={rowRef}
      data-entering={isEntering || undefined}
      data-exiting={isExiting || undefined}
      className={twMerge([
        'flex flex-col sm:flex-row gap-1',
        animated && 'entering:animate-accordion-down',
        animated && 'exiting:animate-accordion-up',
        className,
      ])}
      style={
        {
          '--accordion-padding-bottom': `${ACCORDION_PADDING_BOTTOM}px`,
        } as CSSProperties
      }
    >
      {children}
    </div>
  )
})

function Label({
  children,
  detail,
  className,
}: {
  detail?: boolean
  children?: ReactNode | (() => ReactNode)
  className?: string
}) {
  return (
    <dt className={cn('flex items-center gap-2 min-w-44', className)}>
      {detail ? <CornerDownRight size={12} className='ml-1 text-gray-600 hidden md:block' /> : null}
      {typeof children === 'function' ? (
        children()
      ) : (
        <Typography size='base' className='text-gray-800'>
          {children}
        </Typography>
      )}
    </dt>
  )
}

function capitalize(text: string) {
  return text.slice(0, 1).toUpperCase() + text.slice(1)
}

const DEFAULT_LOADING_CONTENT = <Skeleton />

function Value({
  isLoading = false,
  loadingContent = DEFAULT_LOADING_CONTENT,
  children,
  icon,
  loadingIcon,
  copyValue,
  onCopy,
  filterNoun,
  filterHref,
  onFilter,
}: PropsWithChildren<{
  icon?: ReactNode
  isLoading?: boolean
  loadingContent?: ReactNode
  loadingIcon?: ReactNode
  copyValue?: string
  onCopy?: () => void
  filterNoun?: string
  filterHref?: string
  onFilter?: () => void
}>) {
  const { showToast } = useToast()
  const onCopyToast = useCallback(() => {
    onCopy?.()
    showToast({
      severity: 'success',
      message: filterNoun ? `${capitalize(filterNoun)} copied to clipboard.` : 'Copied to clipboard.',
    })
  }, [filterNoun, onCopy, showToast])
  const onErrorToast = useCallback(() => {
    showToast({
      severity: 'error',
      message: filterNoun
        ? `Failed to copy the ${capitalize(filterNoun)} to clipboard.`
        : 'Failed to copy to clipboard.',
    })
  }, [filterNoun, showToast])

  return (
    <dd
      className={cn(
        'relative group flex grow items-center gap-2 p-2 -m-2',
        copyValue != null ? 'underline underline-offset-2 decoration-1 decoration-dotted decoration-gray-600' : ''
      )}
    >
      {icon && <span className='flex shrink-0 items-center sm:-ml-6'>{isLoading ? loadingIcon : icon}</span>}
      {isLoading ? loadingContent : children}

      {!isLoading && (copyValue || onFilter) ? (
        <div
          className={cn([
            'pointer-events-none',
            'absolute top-1 right-0',
            'opacity-0',
            'group-hover:opacity-100 group-hover:pointer-events-auto',
            'focus-within:opacity-100 focus-within:pointer-events-auto',
            'transition',
            'flex items-center gap-2',
          ])}
        >
          {copyValue ? (
            <CopyButton onCopy={onCopyToast} onError={onErrorToast} copyValue={copyValue} size='xs' />
          ) : null}
          {filterHref || onFilter ? (
            <LinkButton
              variant='secondary'
              size='xs'
              isIcon
              aria-label={`Filter events by this ${filterNoun ?? 'attribute'}`}
              href={filterHref}
              onPress={onFilter}
            >
              <SearchIcon />
            </LinkButton>
          ) : null}
        </div>
      ) : null}
    </dd>
  )
}

function Content(props: ComponentProps<typeof Typography>) {
  return <Typography size='base' weight='medium' {...props} className={props.className} />
}

export function DeviceContent({ event, showDetails }: { event?: IdEventV4; showDetails: boolean }) {
  return (
    <Stack as={Paper} direction={{ default: 'column', lg: 'row' }} gap={6} className='lg:flex-wrap p-6'>
      <Column>
        <SectionLayout>
          <Content>Identification</Content>
          <DeviceInformation event={event} showDetails={showDetails} />
        </SectionLayout>

        <SectionLayout>
          <Content>Client</Content>
          <ClientInformation event={event} showDetails={showDetails} />
        </SectionLayout>
      </Column>
      <Column>
        <SectionLayout>
          <Content>Location</Content>
          <LocationInformation event={event} showDetails={showDetails} />
          <IpLocationMap event={event} />
        </SectionLayout>
      </Column>
    </Stack>
  )
}

function DeviceInformation({ event, showDetails }: { event?: IdEventV4; showDetails: boolean }) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()
  const { subscription } = useCurrentSubscriptionData()
  const highRecallEnabled = subscription?.featureSettings?.highRecallEnabled === true
  const highRecallId = event?.supplementary_id_high_recall?.visitor_id
  const filterParams = usePersistedEventFilterParams()
  const newFilterParams = { ...filterParams, visitorId: event?.identification?.visitor_id ?? '' }
  const environmentName = useGetEnvironmentName(event?.environment_id ?? '')
  const lastSeenDateTime = useNormalizedDateTime(event?.identification?.last_seen_at)
  const firstSeenDateTime = useNormalizedDateTime(event?.identification?.first_seen_at)

  return (
    <>
      <Row>
        <Label>Visitor ID</Label>
        <Value
          isLoading={event?.identification == null}
          loadingContent={<Skeleton width={160} height={16} className='transform-none' />}
          copyValue={event?.identification?.visitor_id}
          filterHref={buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)}
          filterNoun='visitor ID'
          onFilter={() => onQuickFilter(newFilterParams)}
        >
          {event?.identification?.visitor_id ? (
            <VisitorIdLink visitorId={event.identification.visitor_id} workspaceId={subscriptionId} showCopy={false}>
              {({ canNavigate, linkProps, visitorId }) =>
                canNavigate ? (
                  <LinkButtonLink {...linkProps} variant='ghost' className='font-mono font-medium -mx-3 -my-2'>
                    {visitorId}
                  </LinkButtonLink>
                ) : (
                  <Content className='font-mono'>{visitorId}</Content>
                )
              }
            </VisitorIdLink>
          ) : (
            <Content className='font-mono'>—</Content>
          )}
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails}>
        <Label detail>Confidence</Label>
        <Value
          isLoading={event?.identification == null}
          loadingContent={<Skeleton width={32} height={16} className='transform-none' />}
        >
          <Content>
            {event?.identification?.confidence?.score != null
              ? formatPercent(event.identification.confidence.score)
              : 'Not available'}
          </Content>
        </Value>
      </Row>

      {highRecallEnabled && highRecallId && (
        <>
          <Row>
            <Label>
              <WithTooltip
                content='An identifier optimized for recognizing returning users across sessions and devices.'
                wrapperClassName='cursor-help'
              >
                <span>High Recall ID</span>
              </WithTooltip>
            </Label>
            <Value
              copyValue={highRecallId}
              filterHref={buildRoute(
                AppRoute.IdentificationEvents,
                { subscriptionId },
                { ...filterParams, highRecallId }
              )}
              filterNoun='High Recall ID'
              onFilter={() => onQuickFilter({ ...filterParams, highRecallId })}
            >
              <Content className='font-mono'>{highRecallId}</Content>
            </Value>
          </Row>
        </>
      )}

      <Row>
        <Label>Environment</Label>
        <Value
          isLoading={event == null}
          loadingContent={<Skeleton width={160} height={16} className='transform-none' />}
          filterHref={
            event?.environment_id
              ? buildRoute(
                  AppRoute.IdentificationEvents,
                  { subscriptionId },
                  { ...filterParams, environment: event.environment_id }
                )
              : undefined
          }
          filterNoun='environment'
          onFilter={
            event?.environment_id
              ? () => onQuickFilter({ ...filterParams, environment: event.environment_id })
              : undefined
          }
        >
          {event?.environment_id ? (
            environmentName === null ? (
              <Skeleton width={160} height={16} className='transform-none' />
            ) : (
              <Content className={cn(!environmentName && 'font-mono')}>
                {environmentName ?? event.environment_id}
              </Content>
            )
          ) : (
            <Content>—</Content>
          )}
        </Value>
      </Row>

      {event?.proximity?.id && (
        <Row>
          <Label>Proximity ID</Label>
          <Value
            copyValue={event.proximity.id}
            filterHref={buildRoute(
              AppRoute.IdentificationEvents,
              { subscriptionId },
              { ...filterParams, proximityId: event.proximity.id }
            )}
            filterNoun='proximity ID'
            onFilter={() => onQuickFilter({ ...filterParams, proximityId: event?.proximity?.id })}
          >
            <Content className='font-mono'>{event?.proximity?.id}</Content>
          </Value>
        </Row>
      )}

      {event?.proximity?.confidence && (
        <Row shouldBeVisible={showDetails}>
          <Label detail>Confidence</Label>
          <Value
            isLoading={event?.proximity == null}
            loadingContent={<Skeleton width={32} height={16} className='transform-none' />}
          >
            <Content className='font-mono'>{formatPercent(event.proximity.confidence)}</Content>
          </Value>
        </Row>
      )}

      <Row>
        <Label>Last seen</Label>
        <Value
          isLoading={event?.identification == null}
          loadingContent={<Skeleton width={72} height={16} className='transform-none' />}
          copyValue={lastSeenDateTime?.toISO() ?? undefined}
          filterNoun='last seen'
        >
          {lastSeenDateTime && event?.identification?.last_seen_at ? (
            <RelativeTime date={lastSeenDateTime}>
              <Content>{formatDateTimeDistance(event?.identification?.last_seen_at)}</Content>
            </RelativeTime>
          ) : (
            <Content>Not available</Content>
          )}
        </Value>
      </Row>

      <Row>
        <Label>First seen</Label>
        <Value
          isLoading={event?.identification == null}
          loadingContent={<Skeleton width={80} height={16} className='transform-none' />}
          copyValue={firstSeenDateTime?.toISO() ?? undefined}
          filterNoun='first seen'
        >
          {firstSeenDateTime && event?.identification?.first_seen_at ? (
            <RelativeTime date={firstSeenDateTime}>
              <Content>{formatDateTimeDistance(event?.identification?.first_seen_at)}</Content>
            </RelativeTime>
          ) : (
            <Content>Not available</Content>
          )}
        </Value>
      </Row>

      <Row>
        <Label>Replayed</Label>
        <Value
          isLoading={event?.identification == null}
          loadingContent={<Skeleton width={24} height={16} className='transform-none' />}
        >
          <Content>{event?.replayed ? 'Yes' : 'No'}</Content>
        </Value>
      </Row>
    </>
  )
}

function ClientInformation({ event, showDetails = true }: { event?: IdEventV4; showDetails?: boolean }) {
  const browserVersion =
    event?.browser_details?.browser_full_version === 'Not Available'
      ? '(unknown version)'
      : event?.browser_details?.browser_full_version
  const browserValue = event?.browser_details ? event.browser_details.browser_name + ' ' + browserVersion : undefined
  const osValue = event?.browser_details ? event.browser_details.os + ' ' + event.browser_details.os_version : undefined

  const isWeb = event?.sdk?.platform === 'js'
  const isMobile = event?.sdk?.platform === 'ios' || event?.sdk?.platform === 'android'

  // Make sure that the row is rendered while loading so it's not animating when the data is loaded.
  // It disappears when it's not a web request.
  const displayBrowserRow = event == null || isWeb

  return (
    <>
      <Row shouldBeVisible={displayBrowserRow}>
        <Label>Browser</Label>
        <Value
          isLoading={event == null}
          icon={<BrowserIcon type={event?.browser_details?.browser_name} className='size-4' />}
          loadingIcon={<svg className='size-4' />}
          loadingContent={<Skeleton width={96} height={16} className='transform-none' />}
        >
          <Content>{browserValue}</Content>
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails}>
        <Label detail>Incognito</Label>
        <Value
          isLoading={event == null}
          loadingContent={<Skeleton width={48} height={16} className='transform-none' />}
        >
          <Content>{event?.incognito ? 'True' : 'False'}</Content>
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails} className='items-start'>
        <Label detail>User Agent</Label>
        <Value
          isLoading={event == null}
          loadingContent={<Skeleton width='100%' height={96} className='transform-none' />}
          copyValue={event?.user_agent}
          filterNoun='user agent'
        >
          <Content>{event?.user_agent}</Content>
        </Value>
      </Row>

      <Row>
        <Label>Operating system</Label>
        <Value
          isLoading={event == null}
          icon={<OperatingSystemIcon type={event?.browser_details?.os} className='size-4' />}
          loadingIcon={<svg className='size-4' />}
          loadingContent={<Skeleton width={128} height={16} className='transform-none' />}
        >
          <Content>{osValue}</Content>
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails || isMobile}>
        <Label detail={!isMobile}>Device</Label>
        <Value
          isLoading={event == null}
          loadingContent={<Skeleton width={48} height={16} className='transform-none' />}
        >
          <Content>{event?.browser_details?.device ?? 'Other'}</Content>
        </Value>
      </Row>
    </>
  )
}

function IpInformation({
  address,
  geolocation,
  showDetails,
  specifier,
  asn,
  asn_name,
}: {
  address?: string
  geolocation?: GeolocationV4
  showDetails?: boolean
  specifier?: 'v4' | 'v6'
  asn?: string
  asn_name?: string
}) {
  const { currentSubscriptionId: subscriptionId } = useCurrentSubscription()

  const coordinates =
    geolocation && geolocation.longitude != null && geolocation.latitude != null
      ? `${formatNum(geolocation.latitude, true)}° ${geolocation.latitude > 0 ? 'N' : 'S'}, ` +
        `${formatNum(geolocation.longitude, true)}° ${geolocation.longitude > 0 ? 'E' : 'W'}`
      : undefined

  const piiRedacted = address === '0.0.0.0'
  const locationUnavailable = !geolocation || (geolocation.latitude == null && geolocation.longitude == null)

  const filterParams = usePersistedEventFilterParams()
  const newFilterParams = { ...filterParams, ipAddress: address ?? '' }
  const link = piiRedacted ? undefined : buildRoute(AppRoute.IdentificationEvents, { subscriptionId }, newFilterParams)

  return (
    <>
      <Row>
        <Label>IP{specifier} address</Label>
        <Value
          icon={
            geolocation?.country_code ? <Flag countryCode={geolocation.country_code} /> : <div className='h-4 w-5.5' />
          }
          loadingIcon={<div className='h-4 w-5.5' />}
          isLoading={address == null}
          loadingContent={<Skeleton width={100} height={16} className='transform-none' />}
          copyValue={piiRedacted ? '' : address}
          filterHref={link}
          filterNoun='IP address'
          onFilter={() => onQuickFilter(newFilterParams)}
        >
          {piiRedacted ? (
            <WithTooltip
              content='Personally identifiable information (PII) is not available for this workspace. Please contact support for any questions.'
              className='truncate'
            >
              <Content>Not available</Content>
            </WithTooltip>
          ) : (
            <Content>{address}</Content>
          )}
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails && !piiRedacted && !locationUnavailable}>
        <Label detail>Accuracy radius</Label>
        <Value
          isLoading={address == null}
          loadingContent={<Skeleton width={48} height={16} className='transform-none' />}
        >
          <Content>
            {geolocation?.accuracy_radius ?? '-'}
            {geolocation?.accuracy_radius ? ' km' : null}
          </Content>
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails && !piiRedacted && !locationUnavailable}>
        <Label detail>Coordinates</Label>
        <Value
          isLoading={address == null}
          loadingContent={<Skeleton width={160} height={16} className='transform-none' />}
        >
          <Content>{coordinates ?? '-'}</Content>
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails && !piiRedacted && !locationUnavailable}>
        <Label detail>Postal code</Label>
        <Value
          isLoading={address == null}
          loadingContent={<Skeleton width={56} height={16} className='transform-none' />}
        >
          <Content>{geolocation?.postal_code ?? '-'}</Content>
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails && !piiRedacted && !locationUnavailable}>
        <Label detail>Timezone</Label>
        <Value
          isLoading={address == null}
          loadingContent={<Skeleton width={112} height={16} className='transform-none' />}
        >
          <Content>{geolocation?.timezone ?? '-'}</Content>
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails && !piiRedacted && !locationUnavailable}>
        <Label detail>City</Label>
        <Value
          isLoading={address == null}
          loadingContent={<Skeleton width={96} height={16} className='transform-none' />}
        >
          <Content>{geolocation?.city_name ?? '-'}</Content>
        </Value>
      </Row>

      <Row shouldBeVisible={showDetails && !piiRedacted && !locationUnavailable}>
        <Label detail>Country</Label>
        <Value
          isLoading={address == null}
          loadingContent={<Skeleton width={112} height={16} className='transform-none' />}
        >
          <Content>{geolocation?.country_name ?? '-'}</Content>
        </Value>
      </Row>

      {asn && (
        <Row shouldBeVisible={showDetails && !piiRedacted && !locationUnavailable}>
          <Label detail>ASN</Label>
          <Value
            isLoading={asn == null}
            loadingContent={<Skeleton width={112} height={16} className='transform-none' />}
          >
            <Content>
              {asn_name ?? 'Unknown'} ({asn ?? 'N/A'})
            </Content>
          </Value>
        </Row>
      )}
    </>
  )
}

function LocationInformation({ event, showDetails = false }: { event?: IdEventV4; showDetails?: boolean }) {
  const ipInfo = useMemo(() => event?.ip_info, [event?.ip_info])

  return (
    <>
      {ipInfo?.v4 ? (
        <IpInformation
          address={ipInfo.v4.address}
          geolocation={ipInfo.v4.geolocation}
          showDetails={showDetails}
          specifier={ipInfo?.v6 ? 'v4' : undefined}
          asn={ipInfo.v4.asn}
          asn_name={ipInfo.v4.asn_name}
        />
      ) : null}
      {ipInfo?.v6 ? (
        <IpInformation
          address={ipInfo.v6.address}
          geolocation={ipInfo.v6.geolocation}
          showDetails={showDetails}
          specifier={ipInfo?.v4 ? 'v6' : undefined}
          asn={ipInfo.v6.asn}
          asn_name={ipInfo.v6.asn_name}
        />
      ) : null}
      {ipInfo?.v4 == null && ipInfo?.v6 == null && event?.ip_address ? (
        <IpInformation address={event.ip_address} showDetails={showDetails} />
      ) : null}
      {event == null ? <IpInformation showDetails={showDetails} /> : null}
    </>
  )
}
